import React, { Component } from 'react';
import axios from 'axios';
import moment from 'moment';
import Chart from 'chart.js';
import * as ReactBootStrap from 'react-bootstrap';
import '../tools/css/Consultas_programada.css';

const ApiURL = process.env.REACT_APP_API_URL;

// Paleta categórica validada (skill de dataviz) para identificar cada "estado" con el mismo color en todas las gráficas del informe.
const PALETA_CATEGORICA = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const COLOR_MARCA = '#AD1457';
const COLOR_OTROS = '#c3c2b7';
const TEXTO_MUTED = '#898781';
const TEXTO_SECUNDARIO = '#52514e';
const GRID_COLOR = '#e1e0d9';
const EJE_COLOR = '#c3c2b7';

/**
 * Informe de consultas (pestaña "Informes" de Consultas_programada.js).
 * ListExternalConsultation no filtra por fecha ni trae todo de una vez: este
 * componente recorre TODO su paginado (recolectarTodasLasConsultas) y aplica
 * el rango de fechas del propio informe en el cliente, para que las gráficas
 * reflejen todas las consultas del rango, no solo la página que esté viendo
 * el usuario en la pestaña "Consultas realizadas".
 * El padre dispara cargarInforme() por ref al abrir la pestaña, porque los
 * canvas de Chart.js miden 0 si se dibujan dentro de un tab-pane oculto.
 */
class InformeConsultas extends Component {

  chartEstadoRef = React.createRef();
  chartCalificacionRef = React.createRef();
  chartFechaRef = React.createRef();
  chartCalifPorEstadoRef = React.createRef();

  chartEstado = null;
  chartCalificacion = null;
  chartFecha = null;
  chartCalifPorEstado = null;

  // Mapa estable estado -> color: una vez asignado un color a un estado no cambia,
  // aunque el filtro de fechas reduzca los datos (evita "repintar" al filtrar).
  colorPorEstado = {};

  state = {
    fechaDesdeInforme: moment().startOf('month').format('YYYY-MM-DD'),
    fechaHastaInforme: moment().format('YYYY-MM-DD'),
    cargandoInforme: false,
    consultasCargadas: false,
    paginaActualCarga: 0,
    todasLasConsultas: []
  };

  componentWillUnmount(){
    [this.chartEstado, this.chartCalificacion, this.chartFecha, this.chartCalifPorEstado].forEach((grafica) => {
      if (grafica) grafica.destroy();
    });
  }

  // La misma regla que usa la tabla ("Ver" solo aparece con appointmentId != null)
  // se aplica aquí para que el informe cuente exactamente las mismas consultas
  // que se ven en la pestaña "Consultas realizadas".
  obtenerEstadoConsulta = (c) => c.status || 'Sin estado';

  obtenerCalificacion = (c) => {
    const valor = c.score;
    if (valor === null || valor === undefined || valor === '') return null;
    const numero = Number(valor);
    return isNaN(numero) ? null : numero;
  };

  // Consultas "válidas" (mismo criterio que usa toda la tabla: appointmentId
  // presente) de TODAS las páginas, sin aplicar el filtro de fechas del
  // informe. La calificación es un dato informativo del médico: su promedio
  // (acá y en la gráfica/tabla "por estado") siempre es histórico, no se
  // acota por el rango de fechas que sí filtra el resto del informe.
  obtenerConsultasValidasGlobales = () => {
    return (this.state.todasLasConsultas || []).filter((c) => c && c.appointmentId !== null && c.appointmentId !== undefined);
  };

  // KPI informativo del médico: promedio de TODAS las consultas calificadas (score > 0).
  obtenerCalificacionPromedioGlobal = () => {
    const calificaciones = this.obtenerConsultasValidasGlobales().map((c) => this.obtenerCalificacion(c)).filter((valor) => valor !== null && valor > 0);
    if (calificaciones.length === 0) return null;
    return calificaciones.reduce((a, b) => a + b, 0) / calificaciones.length;
  };

  asignarColoresEstadoConsulta = (consultas) => {
    const estadosOrdenados = Array.from(new Set(consultas.map(this.obtenerEstadoConsulta))).sort((a, b) => a.localeCompare(b));
    estadosOrdenados.forEach((estado) => {
      if (!this.colorPorEstado[estado]) {
        const indice = Object.keys(this.colorPorEstado).length;
        this.colorPorEstado[estado] = indice < PALETA_CATEGORICA.length ? PALETA_CATEGORICA[indice] : COLOR_OTROS;
      }
    });
  };

  consultasFiltradas = () => {
    const { todasLasConsultas, fechaDesdeInforme, fechaHastaInforme } = this.state;
    return (todasLasConsultas || []).filter((c) => {
      if (!c || c.appointmentId === null || c.appointmentId === undefined) return false;
      if (fechaDesdeInforme && moment(c.date).isBefore(moment(fechaDesdeInforme, 'YYYY-MM-DD'), 'day')) return false;
      if (fechaHastaInforme && moment(c.date).isAfter(moment(fechaHastaInforme, 'YYYY-MM-DD'), 'day')) return false;
      return true;
    });
  };

  /** Llamado por el padre (por ref) al abrir la pestaña: trae todo el listado solo la primera vez, luego solo redibuja. */
  cargarInforme = () => {
    if (this.state.consultasCargadas) {
      this.dibujarGraficas();
      return;
    }
    this.cargarTodasLasConsultas();
  };

  /** Botón "Actualizar": vuelve a recorrer todo el paginado desde cero. */
  actualizarInforme = () => {
    this.cargarTodasLasConsultas();
  };

  // ListExternalConsultation no soporta filtro por fecha ni trae todo de una
  // vez: se recorre página por página (como exportarExcelConsultas en
  // Consultas_programada.js) hasta que una página vuelva vacía, y el rango de
  // fechas del informe se aplica después, en el cliente, sobre ese total.
  cargarTodasLasConsultas = (pagina = 1, acumulado = []) => {
    const LIMITE_PAGINAS = 500; // salvavidas ante un backend que nunca devuelva página vacía

    this.setState({ cargandoInforme: true, paginaActualCarga: pagina });

    axios.get(ApiURL + "/api/Consultations/ListExternalConsultation?numeropagina=" + pagina)
      .then((response) => {
        const datos = (response.data && response.data.data) || [];
        const acumuladoNuevo = acumulado.concat(datos);

        if (datos.length > 0 && pagina < LIMITE_PAGINAS) {
          this.cargarTodasLasConsultas(pagina + 1, acumuladoNuevo);
        } else {
          this.setState({ todasLasConsultas: acumuladoNuevo, consultasCargadas: true, cargandoInforme: false }, () => this.dibujarGraficas());
        }
      })
      .catch((error) => {
        console.log(error);
        this.setState({ cargandoInforme: false });
      });
  };

  cambiarFechaDesdeInforme = (e) => {
    this.setState({ fechaDesdeInforme: e.target.value }, () => this.dibujarGraficas());
  };

  cambiarFechaHastaInforme = (e) => {
    this.setState({ fechaHastaInforme: e.target.value }, () => this.dibujarGraficas());
  };

  dibujarGraficas = () => {
    const consultas = this.consultasFiltradas();
    this.asignarColoresEstadoConsulta(consultas);
    // La gráfica "por estado" es histórica (independiente del filtro de
    // fechas): también hay que asegurar color para estados que solo
    // aparezcan en el histórico completo y no en la ventana filtrada.
    this.asignarColoresEstadoConsulta(this.obtenerConsultasValidasGlobales());
    this.dibujarGraficaEstadoConsultas(consultas);
    this.dibujarGraficaCalificacion(consultas);
    this.dibujarGraficaFechaConsultas(consultas);
    this.dibujarGraficaCalifPorEstado();
  };

  // Doughnut: identidad = color (no hay eje), por eso lleva leyenda y paleta categórica.
  dibujarGraficaEstadoConsultas = (consultas) => {
    const lienzo = this.chartEstadoRef.current;
    if (!lienzo) return;

    const conteo = {};
    consultas.forEach((c) => {
      const estado = this.obtenerEstadoConsulta(c);
      conteo[estado] = (conteo[estado] || 0) + 1;
    });

    const etiquetas = Object.keys(conteo);
    const datos = etiquetas.map((e) => conteo[e]);
    const colores = etiquetas.map((e) => this.colorPorEstado[e] || COLOR_OTROS);

    if (this.chartEstado) this.chartEstado.destroy();

    this.chartEstado = new Chart(lienzo.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: etiquetas,
        datasets: [{
          data: datos,
          backgroundColor: colores,
          borderColor: '#ffffff',
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 62,
        legend: {
          position: 'bottom',
          labels: { boxWidth: 12, fontColor: TEXTO_SECUNDARIO, fontSize: 12, padding: 14 }
        },
        tooltips: {
          callbacks: {
            label: (item, data) => {
              const etiqueta = data.labels[item.index];
              const valor = data.datasets[0].data[item.index];
              return `${etiqueta}: ${valor} consulta${valor === 1 ? '' : 's'}`;
            }
          }
        }
      }
    });
  };

  // Bar vertical: magnitud sobre un eje ordinal (1 a 5 estrellas), por eso usa
  // un solo color de marca en vez de la paleta categórica de estados.
  // Solo cuenta consultas con score > 0 (dentro del rango de fechas seleccionado);
  // las sin calificar (score nulo/0) quedan fuera, sin un bucket "Sin calificar".
  dibujarGraficaCalificacion = (consultas) => {
    const lienzo = this.chartCalificacionRef.current;
    if (!lienzo) return;

    const buckets = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };
    consultas.forEach((c) => {
      const calif = this.obtenerCalificacion(c);
      if (calif === null || calif <= 0) return;
      const entero = String(Math.max(1, Math.min(5, Math.round(calif))));
      buckets[entero] = (buckets[entero] || 0) + 1;
    });

    const etiquetas = Object.keys(buckets);
    const datos = etiquetas.map((e) => buckets[e]);

    if (this.chartCalificacion) this.chartCalificacion.destroy();

    this.chartCalificacion = new Chart(lienzo.getContext('2d'), {
      type: 'bar',
      data: {
        labels: etiquetas,
        datasets: [{
          label: 'Consultas',
          data: datos,
          backgroundColor: COLOR_MARCA,
          barPercentage: 0.6,
          categoryPercentage: 0.7
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { display: false, drawBorder: true, color: EJE_COLOR },
            ticks: { fontColor: TEXTO_MUTED, fontSize: 11 }
          }],
          yAxes: [{
            gridLines: { color: GRID_COLOR, drawBorder: false, zeroLineColor: EJE_COLOR },
            ticks: { beginAtZero: true, fontColor: TEXTO_MUTED, precision: 0 }
          }]
        },
        tooltips: {
          callbacks: {
            label: (item) => `${item.yLabel} consulta${item.yLabel === 1 ? '' : 's'}`
          }
        }
      }
    });
  };

  // Line: evolución cronológica (una consulta = un punto en el tiempo), color de marca.
  dibujarGraficaFechaConsultas = (consultas) => {
    const lienzo = this.chartFechaRef.current;
    if (!lienzo) return;

    const conteoPorDia = {};
    consultas.forEach((c) => {
      if (!c.date) return;
      const clave = moment(c.date).format('YYYY-MM-DD');
      conteoPorDia[clave] = (conteoPorDia[clave] || 0) + 1;
    });

    const clavesOrdenadas = Object.keys(conteoPorDia).sort();
    const etiquetas = clavesOrdenadas.map((clave) => moment(clave).format('DD/MM'));
    const datos = clavesOrdenadas.map((clave) => conteoPorDia[clave]);

    if (this.chartFecha) this.chartFecha.destroy();

    this.chartFecha = new Chart(lienzo.getContext('2d'), {
      type: 'line',
      data: {
        labels: etiquetas,
        datasets: [{
          label: 'Consultas',
          data: datos,
          borderColor: COLOR_MARCA,
          backgroundColor: 'rgba(173, 20, 87, .12)',
          pointBackgroundColor: COLOR_MARCA,
          pointRadius: 3,
          fill: true,
          lineTension: 0.25
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { display: false, drawBorder: true, color: EJE_COLOR },
            ticks: { fontColor: TEXTO_MUTED, fontSize: 11, maxRotation: 0 }
          }],
          yAxes: [{
            gridLines: { color: GRID_COLOR, drawBorder: false, zeroLineColor: EJE_COLOR },
            ticks: { beginAtZero: true, fontColor: TEXTO_MUTED, precision: 0 }
          }]
        },
        tooltips: {
          callbacks: {
            label: (item) => `${item.yLabel} consulta${item.yLabel === 1 ? '' : 's'}`
          }
        }
      }
    });
  };

  // Bar horizontal: misma identidad de "estado" que el doughnut, mismos colores estables.
  // Cruza estado + calificación para detectar, por ejemplo, estados con mala percepción.
  // Histórico (todas las páginas, score > 0): dato informativo del médico, no se acota por fecha.
  dibujarGraficaCalifPorEstado = () => {
    const lienzo = this.chartCalifPorEstadoRef.current;
    if (!lienzo) return;

    const acumulado = {};
    this.obtenerConsultasValidasGlobales().forEach((c) => {
      const calif = this.obtenerCalificacion(c);
      if (calif === null || calif <= 0) return;
      const estado = this.obtenerEstadoConsulta(c);
      if (!acumulado[estado]) acumulado[estado] = { suma: 0, conteo: 0 };
      acumulado[estado].suma += calif;
      acumulado[estado].conteo += 1;
    });

    const etiquetas = Object.keys(acumulado);
    const datos = etiquetas.map((e) => Number((acumulado[e].suma / acumulado[e].conteo).toFixed(2)));
    const colores = etiquetas.map((e) => this.colorPorEstado[e] || COLOR_OTROS);

    if (this.chartCalifPorEstado) this.chartCalifPorEstado.destroy();

    this.chartCalifPorEstado = new Chart(lienzo.getContext('2d'), {
      type: 'horizontalBar',
      data: {
        labels: etiquetas,
        datasets: [{
          label: 'Calificación promedio',
          data: datos,
          backgroundColor: colores,
          barPercentage: 0.6,
          categoryPercentage: 0.7
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { color: GRID_COLOR, drawBorder: false, zeroLineColor: EJE_COLOR },
            ticks: { beginAtZero: true, max: 5, fontColor: TEXTO_MUTED }
          }],
          yAxes: [{
            gridLines: { display: false, drawBorder: true, color: EJE_COLOR },
            ticks: { fontColor: TEXTO_SECUNDARIO, fontSize: 12 }
          }]
        },
        tooltips: {
          callbacks: {
            label: (item) => `${item.xLabel} / 5`
          }
        }
      }
    });
  };

  render(){
    const consultas = this.consultasFiltradas();
    const total = consultas.length;
    const finalizadas = consultas.filter((c) => c.status === 'finalizada').length;
    const porcentajeFinalizadas = total ? (finalizadas / total * 100) : 0;
    const calificacionPromedioGlobal = this.obtenerCalificacionPromedioGlobal();

    // "Cantidad" respeta el filtro de fechas del informe (igual que el resto
    // de la tabla); el cruce por calificación es histórico (score > 0, todas
    // las páginas), igual que el KPI y la gráfica "por estado": agrupa, para
    // cada estado, cuántas consultas tienen cada score (1 a 5) encontrado.
    const resumenPorEstado = {};
    consultas.forEach((c) => {
      const estado = this.obtenerEstadoConsulta(c);
      if (!resumenPorEstado[estado]) resumenPorEstado[estado] = { cantidad: 0, porScore: {} };
      resumenPorEstado[estado].cantidad += 1;
    });
    const scoresEncontrados = new Set();
    this.obtenerConsultasValidasGlobales().forEach((c) => {
      const calif = this.obtenerCalificacion(c);
      if (calif === null || calif <= 0) return;
      const estado = this.obtenerEstadoConsulta(c);
      const entero = Math.max(1, Math.min(5, Math.round(calif)));
      scoresEncontrados.add(entero);
      if (!resumenPorEstado[estado]) resumenPorEstado[estado] = { cantidad: 0, porScore: {} };
      resumenPorEstado[estado].porScore[entero] = (resumenPorEstado[estado].porScore[entero] || 0) + 1;
    });
    const columnasScore = Array.from(scoresEncontrados).sort((a, b) => a - b);

    return (
      <div className="informe-consultas">

        <div className="row consultas-toolbar">
          <div className="col-sm-3">
            <div className="input-group input-group-sm mb-3">
              <div className="input-group-prepend">
                <div className="input-group-text">Desde</div>
              </div>
              <input type="date" className="form-control" value={this.state.fechaDesdeInforme} onChange={this.cambiarFechaDesdeInforme}/>
            </div>
          </div>
          <div className="col-sm-3">
            <div className="input-group input-group-sm mb-3">
              <div className="input-group-prepend">
                <div className="input-group-text">Hasta</div>
              </div>
              <input type="date" className="form-control" value={this.state.fechaHastaInforme} onChange={this.cambiarFechaHastaInforme}/>
            </div>
          </div>
          <div className="col-sm-3 d-flex align-items-start">
            <button type="button" className="btn btn-primary btn-sm mb-3" disabled={this.state.cargandoInforme} onClick={this.actualizarInforme}>
              {this.state.cargandoInforme ? <ReactBootStrap.Spinner animation="border" size="sm"/> : <i className="fas fa-sync-alt"></i>} Actualizar
            </button>
          </div>
        </div>

        {this.state.cargandoInforme ? (
          <p className="consultas-nota">Cargando consultas (página {this.state.paginaActualCarga}) — {this.state.todasLasConsultas.length} acumuladas...</p>
        ) : null}

        <div className="row consultas-kpis">
          <div className="col-md-4">
            <div className="consultas-stat">
              <span className="consultas-stat-label">Total consultas</span>
              <span className="consultas-stat-value">{total.toLocaleString('es-CO')}</span>
            </div>
          </div>
          <div className="col-md-4">
            <div className="consultas-stat">
              <span className="consultas-stat-label" title="Promedio histórico: todas las consultas calificadas, sin importar el rango de fechas seleccionado.">Calificación promedio (histórica)</span>
              <span className="consultas-stat-value">{calificacionPromedioGlobal!==null ? calificacionPromedioGlobal.toFixed(1) : 'Sin calificación'}</span>
            </div>
          </div>
          <div className="col-md-4">
            <div className="consultas-stat">
              <span className="consultas-stat-label">% Finalizadas</span>
              <span className="consultas-stat-value">{porcentajeFinalizadas.toFixed(0)}%</span>
            </div>
          </div>
        </div>

        <div className="row consultas-graficas">

          <div className="col-md-5">
            <div className="card">
              <div className="card-header"><h3 className="card-title">Consultas por estado</h3></div>
              <div className="card-body">
                <div className="consultas-canvas-wrap">
                  <canvas ref={this.chartEstadoRef}></canvas>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-7">
            <div className="card">
              <div className="card-header"><h3 className="card-title">Consultas por calificación</h3></div>
              <div className="card-body">
                <div className="consultas-canvas-wrap">
                  <canvas ref={this.chartCalificacionRef}></canvas>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="card">
              <div className="card-header"><h3 className="card-title">Número de consultas por día</h3></div>
              <div className="card-body">
                <div className="consultas-canvas-wrap consultas-canvas-wrap-lg">
                  <canvas ref={this.chartFechaRef}></canvas>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="card">
              <div className="card-header"><h3 className="card-title" title="Histórica: todas las consultas calificadas, sin importar el rango de fechas seleccionado.">Calificación promedio por estado de consulta</h3></div>
              <div className="card-body">
                <div className="consultas-canvas-wrap">
                  <canvas ref={this.chartCalifPorEstadoRef}></canvas>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="card table-responsive">
              <div className="card-header"><h3 className="card-title">Resumen por estado</h3></div>
              <div className="card-body">
                <div className="consultas-table-wrap">
                <table className="table table-bordered table-striped table-hover consultas-table">
                  <thead>
                    <tr>
                      <th>Estado</th>
                      <th>Cantidad</th>
                      {columnasScore.map((score) => (
                        <th key={score} title="Histórica: todas las consultas calificadas, sin importar el rango de fechas seleccionado.">Calificación {score} (histórica)</th>
                      ))}
                      {columnasScore.length===0 ? <th>Calificación</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(resumenPorEstado).map((estado) => (
                      <tr key={estado}>
                        <td>{estado}</td>
                        <td>{resumenPorEstado[estado].cantidad}</td>
                        {columnasScore.map((score) => (
                          <td key={score}>{resumenPorEstado[estado].porScore[score] || 0}</td>
                        ))}
                        {columnasScore.length===0 ? <td>Sin calificación</td> : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="consultas-nota">Este informe recorre todas las páginas del listado de consultas y aplica el filtro de fechas sobre el total; usa "Actualizar" para traer de nuevo todo el listado si se registraron consultas nuevas.</p>

      </div>
    );
  }
}

export default InformeConsultas;
